import { useState, useEffect } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Separator } from '@/components/ui/separator'
import { Switch } from '@/components/ui/switch'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import {
  Settings,
  Calculator,
  Save,
  RotateCcw,
  Wallet,
  Receipt,
  CreditCard,
  Gift,
  Info,
  Mail
} from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useConfigStore } from '@/stores/configStore'
import { useUnsavedChanges } from '@/hooks/useUnsavedChanges'
import { TaxCalculationConfig } from '@/components/config/TaxCalculationConfig'
import { TieredRevenueShareConfig } from '@/components/config/TieredRevenueShareConfig'
import { RevenueShareTier } from '@/types'
import { CalculationPreview } from '@/components/config/CalculationPreview'
import { sv } from '@/lib/constants'
import { formatPercentFromWhole } from '@/lib/formatters'
import { safeParseNumber, validatePercentage } from '@/lib/validators'
import { toast } from 'sonner'
import {
  PageContainer,
  PageHeader
} from '@/components/common'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'

/** Default email template - must match backend DEFAULT_SALARY_EMAIL_TEMPLATE */
const DEFAULT_SALARY_EMAIL_TEMPLATE = `Hej {förarnamn},

Här kommer din lönespecifikation bifogad.

Vänliga hälsningar,
{företagsnamn}`

export function ConfigurationPage() {
  const { currentUser: user } = useAuth()
  const {
    settings,
    loading,
    loadSettings,
    updateSettings,
    resetToDefaults,
    taxTableLoading,
    taxTableLoaded,
    loadTaxTableIfNeeded,
    getAvailableTables
  } = useConfigStore()
  const [formData, setFormData] = useState(settings)
  const [hasChanges, setHasChanges] = useState(false)
  const [availableTables, setAvailableTables] = useState<number[]>([31])

  // Toggle for inclusive/exclusive vacation pay in driver share input
  const [includeVacationInShare, setIncludeVacationInShare] = useState(() => {
    const saved = localStorage.getItem('includeVacationInShare')
    return saved ? JSON.parse(saved) : false
  })

  // Store raw input values to allow empty fields during editing
  const [rawInputs, setRawInputs] = useState({
    driverRevenueShare: settings.driverRevenueShare.toString(),
    vacationPayRate: settings.vacationPayRate.toString(),
    taxRate: settings.taxRate.toString(),
    vatRate: settings.vatRate.toString(),
    employerTaxRate: settings.employerTaxRate.toString()
  })

  useUnsavedChanges(hasChanges)

  // Helper functions for conversion between inclusive/exclusive vacation pay
  const convertToExclusive = (inclusivePercent: number, vacationRate: number): number => {
    return inclusivePercent / (1 + vacationRate / 100)
  }

  const convertToInclusive = (exclusivePercent: number, vacationRate: number): number => {
    return exclusivePercent * (1 + vacationRate / 100)
  }

  const handleInputChange = (field: keyof typeof formData, value: number | boolean | string | RevenueShareTier[] | undefined) => {
    if (typeof value === 'string') {
      if (field === 'driverRevenueShare' || field === 'vacationPayRate' || field === 'taxRate' || field === 'vatRate' || field === 'employerTaxRate') {
        setRawInputs(prev => ({ ...prev, [field]: value }))

        const numericValue = value === '' ? 0 : safeParseNumber(value)
        let validatedValue = validatePercentage(numericValue)

        if (field === 'driverRevenueShare' && includeVacationInShare) {
          const exclusiveShare = convertToExclusive(validatedValue, formData.vacationPayRate)
          validatedValue = validatePercentage(exclusiveShare)
        }

        const newData = { ...formData, [field]: validatedValue }
        setFormData(newData)
        setHasChanges(JSON.stringify(newData) !== JSON.stringify(settings))
      } else {
        const newData = { ...formData, [field]: value }
        setFormData(newData)
        setHasChanges(JSON.stringify(newData) !== JSON.stringify(settings))
      }
    } else if (Array.isArray(value)) {
      // Handle RevenueShareTier[] arrays
      const newData = { ...formData, [field]: value }
      setFormData(newData)
      setHasChanges(JSON.stringify(newData) !== JSON.stringify(settings))
    } else {
      const newData = { ...formData, [field]: value }
      setFormData(newData)
      setHasChanges(JSON.stringify(newData) !== JSON.stringify(settings))
    }
  }

  const handleToggleVacationInclusion = (checked: boolean) => {
    setIncludeVacationInShare(checked)
    localStorage.setItem('includeVacationInShare', JSON.stringify(checked))

    if (checked) {
      const inclusiveValue = convertToInclusive(formData.driverRevenueShare, formData.vacationPayRate)
      setRawInputs(prev => ({ ...prev, driverRevenueShare: inclusiveValue.toFixed(2) }))
    } else {
      setRawInputs(prev => ({ ...prev, driverRevenueShare: formData.driverRevenueShare.toFixed(2) }))
    }
  }

  const handleDriverShareBlur = () => {
    const value = parseFloat(rawInputs.driverRevenueShare)
    if (!isNaN(value)) {
      setRawInputs(prev => ({ ...prev, driverRevenueShare: value.toFixed(2) }))
    }
  }

  // Load available tables when component mounts
  useEffect(() => {
    const loadTables = async () => {
      try {
        const tables = await getAvailableTables()
        setAvailableTables(tables.length > 0 ? tables : [31])
      } catch (error) {
        console.warn('Failed to load available tables, using default:', error)
        setAvailableTables([31])
      }
    }
    loadTables()
  }, [getAvailableTables])

  // Sync formData and rawInputs when settings change
  useEffect(() => {
    setFormData(settings)

    const driverShareDisplay = includeVacationInShare
      ? convertToInclusive(settings.driverRevenueShare, settings.vacationPayRate).toFixed(2)
      : settings.driverRevenueShare.toString()

    setRawInputs({
      driverRevenueShare: driverShareDisplay,
      vacationPayRate: settings.vacationPayRate.toString(),
      taxRate: settings.taxRate.toString(),
      vatRate: settings.vatRate.toString(),
      employerTaxRate: settings.employerTaxRate.toString()
    })
    setHasChanges(false)
  }, [settings])

  // Update displayed driver share when vacation pay changes
  useEffect(() => {
    if (includeVacationInShare) {
      const inclusiveValue = convertToInclusive(formData.driverRevenueShare, formData.vacationPayRate)
      setRawInputs(prev => ({
        ...prev,
        driverRevenueShare: inclusiveValue.toFixed(2)
      }))
    }
  }, [formData.vacationPayRate, includeVacationInShare])

  // Auto-load tax table for current SAVED settings
  useEffect(() => {
    if (settings.taxCalculationMethod === 'table') {
      const tableName = settings.taxTableName ?? 'skattetabell31'
      if (!taxTableLoaded) {
        loadTaxTableIfNeeded(settings.taxCalculationMethod, tableName)
          .then(() => {
            console.log(`Tax table ${tableName} loaded for current saved settings`)
          })
          .catch(() => {
            console.warn('Could not load tax table for saved settings')
          })
      }
    }
  }, [settings.taxCalculationMethod, settings.taxTableName, loadTaxTableIfNeeded, taxTableLoaded])

  const handleSave = async () => {
    if (!user) {
      toast.error('Du måste vara inloggad för att spara inställningar')
      return
    }
    try {
      const taxTableChanged = formData.taxTableName !== settings.taxTableName

      await updateSettings(user, formData)
      setHasChanges(false)

      await loadSettings(user)

      if (taxTableChanged && formData.taxCalculationMethod === 'table') {
        try {
          await loadTaxTableIfNeeded(formData.taxCalculationMethod, formData.taxTableName)
          toast.success('Inställningar sparade! Skattetabell uppdaterad.')
        } catch (tableError) {
          console.warn('Failed to reload tax table after settings save:', tableError)
          toast.success('Inställningar sparade! (Skattetabell laddas...)')
        }
      } else {
        toast.success('Inställningar sparade!')
      }
    } catch (error) {
      toast.error('Kunde inte spara inställningar')
    }
  }

  const handleReset = async () => {
    if (!user) {
      toast.error('Du måste vara inloggad för att återställa inställningar')
      return
    }
    try {
      await resetToDefaults(user)
      setFormData(settings)
      setHasChanges(false)
      toast.success('Inställningar återställda till standardvärden')
    } catch (error) {
      toast.error('Kunde inte återställa inställningar')
    }
  }

  return (
    <PageContainer>
      <PageHeader
        title={sv.config.title}
        subtitle="Konfigurera dina löneberäkningar"
      />

      <div className="max-w-4xl mx-auto">
        <Tabs defaultValue="settings" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="settings">
              <Settings className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Inställningar</span>
            </TabsTrigger>
            <TabsTrigger value="preview">
              <Calculator className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Beräkning</span>
            </TabsTrigger>
          </TabsList>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-3">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {/* Section 1: Förarlön */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-muted">
                    <Wallet className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <CardTitle>Förarlön</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-3">
                    {/* Vacation toggle - only show in single mode */}
                    {(formData.revenueShareMode ?? 'single') === 'single' && (
                      <div className="flex items-center justify-between p-2.5 bg-muted/20 rounded-lg border border-border/50">
                        <Label htmlFor="includeVacation" className="text-sm font-medium cursor-pointer">
                          Inkludera semester i förarandel
                        </Label>
                        <Switch
                          id="includeVacation"
                          checked={includeVacationInShare}
                          onCheckedChange={handleToggleVacationInclusion}
                        />
                      </div>
                    )}

                    {/* Tiered Revenue Share Config */}
                    <TieredRevenueShareConfig
                      value={{
                        driverRevenueShare: formData.driverRevenueShare,
                        revenueShareMode: formData.revenueShareMode,
                        revenueShareTiers: formData.revenueShareTiers
                      }}
                      onChange={(field, value) => handleInputChange(field as keyof typeof formData, value)}
                      disabled={loading}
                      rawInputs={{ driverRevenueShare: rawInputs.driverRevenueShare }}
                      onRawInputChange={(field, value) => {
                        setRawInputs(prev => ({ ...prev, [field]: value }))
                      }}
                      onDriverShareBlur={handleDriverShareBlur}
                      includeVacationInShare={includeVacationInShare}
                      displayLabel={includeVacationInShare ? 'Förarandel (inkl. semester) (%)' : undefined}
                    />

                    {/* Show exclusive share info in single mode with vacation */}
                    {(formData.revenueShareMode ?? 'single') === 'single' && includeVacationInShare && (
                      <p className="text-xs text-muted-foreground -mt-2">
                        Exkl. semester: {formatPercentFromWhole(formData.driverRevenueShare)}
                      </p>
                    )}

                  {/* Vacation Pay */}
                  <div className="space-y-1.5">
                    <Label htmlFor="vacationPay" className="font-medium">Semesterersättning (%)</Label>
                    <Input
                      id="vacationPay"
                      type="number"
                      value={rawInputs.vacationPayRate}
                      onChange={(e) => handleInputChange('vacationPayRate', e.target.value)}
                      min="0"
                      max="50"
                      step="0.1"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Section 2: Skatt */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-muted">
                    <Receipt className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <CardTitle>Skatt</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <TaxCalculationConfig
                  value={{
                    taxCalculationMethod: formData.taxCalculationMethod,
                    taxRate: formData.taxRate,
                    taxTableName: formData.taxTableName,
                    taxColumn: formData.taxColumn
                  }}
                  onChange={(field, value) => handleInputChange(field as keyof typeof formData, value)}
                  availableTables={availableTables}
                  taxTableLoading={taxTableLoading}
                  taxTableLoaded={taxTableLoaded}
                  disabled={false}
                  rawInputs={{ taxRate: rawInputs.taxRate }}
                  onRawInputChange={(field, value) => {
                    setRawInputs(prev => ({ ...prev, [field]: value }))
                  }}
                />
              </CardContent>
            </Card>

            {/* Section 3: Avgifter */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-muted">
                    <CreditCard className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <CardTitle>Avgifter</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-3">
                  {/* VAT */}
                  <div className="space-y-1.5">
                    <Label htmlFor="vatRate" className="font-medium">Moms (%)</Label>
                    <Input
                      id="vatRate"
                      type="number"
                      value={rawInputs.vatRate}
                      onChange={(e) => handleInputChange('vatRate', e.target.value)}
                      min="0"
                      max="25"
                      step="0.1"
                    />
                  </div>

                  {/* Employer Tax */}
                  <div className="space-y-1.5">
                    <Label htmlFor="employerTaxRate" className="font-medium">Arbetsgivaravgift (%)</Label>
                    <Input
                      id="employerTaxRate"
                      type="number"
                      value={rawInputs.employerTaxRate}
                      onChange={(e) => handleInputChange('employerTaxRate', e.target.value)}
                      min="0"
                      max="100"
                      step="0.01"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Section 4: Dricks */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-muted">
                    <Gift className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <CardTitle>Dricks</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-3">
                  {/* Tips Handling */}
                  <div className="space-y-2.5">
                    <Label className="font-medium">Hantering av dricks</Label>
                    <RadioGroup
                      value={
                        formData.feeOnTips === true ? 'fleet_commission' :
                        formData.feeOnTips === false ? 'driver_share' :
                        formData.feeOnTips
                      }
                      onValueChange={(value) => handleInputChange('feeOnTips', value)}
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="fleet_commission" id="fleet_commission" />
                        <Label htmlFor="fleet_commission" className="text-sm font-normal cursor-pointer">
                          Åkaravgift på dricks
                        </Label>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            <p className="text-xs">Dricks ingår i nettointäkterna och delas enligt åkaravgift (60/40). Semesterersättning beräknas på hela beloppet inkl. dricks.</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>

                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="driver_share" id="driver_share" />
                        <Label htmlFor="driver_share" className="text-sm font-normal cursor-pointer">
                          Dricks utan åkaravgift
                        </Label>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            <p className="text-xs">Föraren behåller 100% av dricksen. Dricks visas som separat rad på lönespecifikationen. Ingen semesterersättning på dricks.</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>

                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="separate_untaxed" id="separate_untaxed" />
                        <Label htmlFor="separate_untaxed" className="text-sm font-normal cursor-pointer">
                          Separat oskattat dricks
                        </Label>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            <p className="text-xs">Föraren behåller 100% av dricksen och den betalas ut utan skatteavdrag. Ingen semesterersättning på dricks.</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    </RadioGroup>
                  </div>
                </div>

                <Separator className="my-3" />

                {/* Report Settings */}
                <div className="space-y-2.5">
                  <Label className="font-medium">Rapportinställningar</Label>
                  <div className="flex items-center justify-between p-2.5 bg-muted/20 rounded-lg border border-border/50">
                    <div className="flex-1">
                      <Label htmlFor="includeSalaryCalculationPage" className="text-sm font-medium cursor-pointer">
                        Inkludera beräkningsunderlag i lönespecifikationer
                      </Label>
                      <p className="text-xs text-muted-foreground mt-1">
                        Visa detaljerad beräkningssida (sida 2) i PDF-lönespecifikationer
                      </p>
                    </div>
                    <Switch
                      id="includeSalaryCalculationPage"
                      checked={formData.includeSalaryCalculationPage ?? true}
                      onCheckedChange={(checked) => handleInputChange('includeSalaryCalculationPage', checked)}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
            </div>

            {/* Section 5: E-postmeddelande */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-muted">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <CardTitle>E-postmeddelande</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="salaryEmailTemplate" className="font-medium">
                    Meddelande för lönespecifikationer
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Anpassa meddelandet som skickas med lönespecifikationer. Använd {'{förarnamn}'} och {'{företagsnamn}'} som variabler.
                  </p>
                  <Textarea
                    id="salaryEmailTemplate"
                    value={formData.salaryEmailTemplate ?? DEFAULT_SALARY_EMAIL_TEMPLATE}
                    onChange={(e) => handleInputChange('salaryEmailTemplate', e.target.value || undefined)}
                    placeholder={DEFAULT_SALARY_EMAIL_TEMPLATE}
                    rows={6}
                    className="font-mono text-sm"
                  />
                  <div className="flex justify-end">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleInputChange('salaryEmailTemplate', undefined)}
                      disabled={!formData.salaryEmailTemplate}
                      className="text-xs"
                    >
                      <RotateCcw className="h-3 w-3 mr-1" />
                      Återställ till standard
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Save/Reset Buttons */}
            <div className="flex flex-col sm:flex-row justify-between gap-3">
              <Button variant="outline" onClick={handleReset} disabled={loading} className="gap-2">
                <RotateCcw className="h-4 w-4" />
                Återställ
              </Button>
              <Button onClick={handleSave} disabled={loading || !hasChanges} className="gap-2">
                <Save className="h-4 w-4" />
                {loading ? 'Sparar...' : 'Spara'}
              </Button>
            </div>
          </TabsContent>

        {/* Preview Tab */}
        <TabsContent value="preview" className="space-y-4">
          <CalculationPreview
            settings={settings}
            taxTableLoaded={taxTableLoaded}
          />
        </TabsContent>
        </Tabs>
      </div>
    </PageContainer>
  )
}
